import { faUser } from "@fortawesome/pro-solid-svg-icons";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Pill } from "@aviary/components/Pill";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { l } from "@aviary/locales/i18n";
import type { AviarySize } from "@aviary/types";
import { FontAwesomeIcon } from "@shared/react-fontawesome";

import { DeprecatedAvatar } from "./DeprecatedAvatar/DeprecatedAvatar";
import { PlatformBadge } from "./PlatformBadge";

import * as styles from "./Avatar.styles";

type AviaryAvatarSize = Exclude<AviarySize, "xxsmall" | "none" | "normal">;

interface AvatarProps {
  /**
   * Profile photo (if uploaded) for the user
   */
  image?: string;
  /**
   * User's first name
   */
  firstName?: string;
  /**
   * User's last name
   */
  lastName?: string;
  /**
   * Specify if the avatar is using the "light" profile, has a white background with dark text. To be used on dark backgrounds only
   *
   * @default false
   */
  isLight?: boolean;
  /**
   * size of the Avatar
   */
  size?: AviaryAvatarSize;
  /**
   * Label, required for aria purposes when an image is present
   */
  label: string;
  /**
   * Specify if the avatar is for a Wellevate user
   * Overlays Wellevate platform badge on the avatar
   * @default false
   */
  isWellevate?: boolean;
  /**
   * Indicates the status of the user, if archived or blocked
   *
   */
  userStatus?: "archived" | "blocked";
  /**
   * Position of the children content overlayed on the Avatar
   */
  overlayPosition?: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" | "center";
  /**
   * Children content to be overlayed on the Avatar
   */
  children?: ReactNode;
}

const Avatar = (props: AvatarProps) => {
  const { t } = useTranslation();
  const isDS4Enabled = useIsDS4Enabled();

  if (!isDS4Enabled) return <DeprecatedAvatar {...props} />;

  const {
    image,
    firstName,
    lastName,
    label,
    size,
    isWellevate,
    overlayPosition,
    // isLight only is used for DS3 version, so we're extracting it out
    // here to ensure no DOM errors
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLight,
    userStatus,
    children,
    ...rest
  } = props;

  const wrapperStyles = [
    styles.avatar,
    size && styles.sizes[size],
    userStatus && !image && styles.avatarWithStatus,
  ];

  const overlayChipStyles = [
    styles.overlayChip,
    overlayPosition && styles.overlayPosition[overlayPosition](size, isWellevate),
  ];

  const imageStyles = [styles.image, userStatus && styles.avatarWithStatus];

  const canRenderName = firstName && lastName && firstName.length > 0 && lastName.length > 0;

  const getNonAvatarContents = () => {
    if (canRenderName) {
      const initials = firstName[0] + lastName[0];

      return initials.toUpperCase();
    }

    return <FontAwesomeIcon icon={faUser} />;
  };

  const renderStatusPill = () => {
    if (!userStatus) return null;

    if (userStatus === "archived") {
      return (
        <div css={styles.userStatusPosition}>
          <Pill isColor="warning" size="xsmall">
            {t(l.aviary.avatar.archived)}
          </Pill>
        </div>
      );
    }

    return (
      <div css={styles.userStatusPosition}>
        <Pill isColor="danger" size="xsmall">
          {t(l.aviary.avatar.blocked)}
        </Pill>
      </div>
    );
  };

  const renderOverlayChip = () => {
    if (isWellevate && !children) {
      return (
        <div css={overlayChipStyles}>
          <PlatformBadge size={size} />
        </div>
      );
    }

    return children && <div css={overlayChipStyles}>{children}</div>;
  };

  const renderImageOrName = () => {
    if (image) {
      return <img src={image} css={imageStyles} alt={label} aria-hidden={!label} />;
    }

    return <span>{getNonAvatarContents()}</span>;
  };

  return (
    <div css={wrapperStyles} {...rest}>
      {renderImageOrName()}
      {renderStatusPill()}
      {renderOverlayChip()}
    </div>
  );
};

Avatar.defaultProps = {
  overlayPosition: "bottomLeft",
  size: "medium",
  isWellevate: false,
};

export { Avatar, type AvatarProps, type AviaryAvatarSize };
